import { db } from "./dbClient";

import { useState, useRef, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { callAI } from "./aiClient";
import {
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer,
} from "recharts";

import { useTheme } from "./theme";

// ─── Load all module data for AI context ─────────────────────────────────────
async function loadAllModuleData(companyId) {
  const safe = (p) => Promise.resolve(p)
    .then(r => ({ data: r?.data ?? [], error: r?.error ?? null }))
    .catch(() => ({ data: [], error: null }));
  const byCompany = (q) => companyId ? q.eq("company_id", companyId) : q;
  const lsGet = (key, fallback = []) => { try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : fallback; } catch { return fallback; } };

  const [
    { data: cases },
    { data: sarDrafts },
    { data: eddReviews },
    { data: kycSubmissions },
    { data: sanctionsScreenings },
    { data: trainingModules },
    { data: trainingProgress },
    { data: transactions },
    { data: ctrFilings },
    { data: watchlistEntries },
  ] = await Promise.all([
    safe(byCompany(db.from("cases").select("*"))),
    safe(byCompany(db.from("sar_drafts").select("*"))),
    safe(byCompany(db.from("edd_reviews").select("*"))),
    safe(byCompany(db.from("kyc_submissions").select("*"))),
    safe(byCompany(db.from("sanctions_screening").select("*"))),
    safe(byCompany(db.from("training_modules").select("*"))),
    safe(byCompany(db.from("training_progress").select("*"))),
    safe(byCompany(db.from("transactions").select("*"))),
    safe(byCompany(db.from("ctr_filings").select("*"))),
    safe(byCompany(db.from("internal_watchlist").select("*"))),
  ]);

  const ctrArr     = ctrFilings      || [];
  const watchArr   = watchlistEntries || [];

  const trTransactions = lsGet("tr_transactions");
  const trCaseFiles    = lsGet("tr_casefiles");
  const trUnhosted     = lsGet("tr_unhosted");
  const tmAlerts       = lsGet("crossregguard_tm_alerts");

  const count = (arr, key) => (arr || []).reduce((a, x) => { const v = x?.[key] || "Unknown"; a[v] = (a[v] || 0) + 1; return a; }, {});
  const pct   = (n, total) => total ? `${Math.round(n / total * 100)}%` : "0%";

  const casesArr   = cases   || [];
  const sarArr     = sarDrafts || [];
  const eddArr     = eddReviews || [];
  const kycArr     = kycSubmissions || [];
  const sanctArr   = sanctionsScreenings || [];
  const trainMods  = trainingModules || [];
  const trainProg  = trainingProgress || [];
  const txArr      = transactions || [];

  return {
    cases: {
      total: casesArr.length,
      byStatus:   count(casesArr, "status"),
      byPriority: count(casesArr, "priority"),
      byType:     count(casesArr, "type"),
      byCountry:  count(casesArr, "country"),
      byRisk:     count(casesArr, "risk_rating"),
      recentCount: casesArr.filter(c => { try { return new Date(c.created_at) > new Date(Date.now() - 30*86400000); } catch { return false; } }).length,
    },
    sar: {
      total: sarArr.length,
      byStatus:       count(sarArr, "status"),
      byActivityType: count(sarArr, "activity_type"),
      byJurisdiction: count(sarArr, "jurisdiction"),
      filed:    sarArr.filter(s => s.status === "filed" || s.filing_date).length,
      pending:  sarArr.filter(s => s.status === "pending" || s.status === "draft").length,
      recentCount: sarArr.filter(s => { try { return new Date(s.created_at) > new Date(Date.now() - 30*86400000); } catch { return false; } }).length,
    },
    edd: {
      total: eddArr.length,
      byStatus:       count(eddArr, "status"),
      byRisk:         count(eddArr, "risk_level"),
      byJurisdiction: count(eddArr, "jurisdiction"),
      highRisk: eddArr.filter(e => e.risk_level === "high" || e.risk_level === "High").length,
    },
    kyc: {
      total: kycArr.length,
      byStatus:          count(kycArr, "status"),
      byRisk:            count(kycArr, "risk_rating"),
      byEntityType:      count(kycArr, "entity_type"),
      byJurisdiction:    count(kycArr, "jurisdiction"),
      byScreeningStatus: count(kycArr, "screening_status"),
      approved:  kycArr.filter(k => k.status === "approved").length,
      pending:   kycArr.filter(k => k.status === "pending" || k.status === "under_review").length,
      rejected:  kycArr.filter(k => k.status === "rejected").length,
      highRisk:  kycArr.filter(k => k.risk_rating === "high" || k.risk_rating === "High").length,
      approvalRate: pct(kycArr.filter(k => k.status === "approved").length, kycArr.length),
    },
    sanctions: {
      total:           sanctArr.length,
      hits:            sanctArr.filter(s => s.result === "hit" || s.result === "match").length,
      clear:           sanctArr.filter(s => s.result === "clear" || s.result === "no_match").length,
      byList:          count(sanctArr, "list_type"),
      hitRate:         pct(sanctArr.filter(s => s.result === "hit" || s.result === "match").length, sanctArr.length),
      // False positive tracking: hits that were reviewed and confirmed vs dismissed
      confirmedHits:   sanctArr.filter(s => s.review_status === "confirmed").length,
      falsePositives:  sanctArr.filter(s => s.review_status === "false_positive").length,
      reviewed:        sanctArr.filter(s => s.review_status != null).length,
      falsePositiveRate: pct(
        sanctArr.filter(s => s.review_status === "false_positive").length,
        sanctArr.filter(s => s.review_status != null).length
      ),
    },
    training: (() => {
      const completed   = trainProg.filter(p => p.quiz_passed === true);
      // training_modules may be empty if modules are preloaded in UI — infer from progress records
      const distinctModuleIds = new Set(trainProg.map(p => p.module_id)).size;
      const effectiveModules  = trainMods.length || distinctModuleIds;
      return {
        totalModules:    effectiveModules,
        activeModules:   trainMods.filter(m => m.status === "active" || m.status === "published").length,
        byCategory:      count(trainMods, "category"),
        totalCompletions: completed.length,
        totalEnrolled:   trainProg.length,
        avgScore:        null,
        completionRate:  pct(completed.length, trainProg.length),
        completedByUserId: completed.reduce((acc, p) => {
          acc[p.user_id] = (acc[p.user_id] || 0) + 1;
          return acc;
        }, {}),
        enrolledByUserId: trainProg.reduce((acc, p) => {
          acc[p.user_id] = (acc[p.user_id] || 0) + 1;
          return acc;
        }, {}),
      };
    })(),
    transactions: {
      total:    txArr.length,
      byStatus: count(txArr, "status"),
      byType:   count(txArr, "type"),
      highRisk: txArr.filter(t => (t.risk_score || 0) >= 70).length,
    },
    ctr: {
      total:    ctrArr.length,
      byStatus: count(ctrArr, "status"),
      filed:    ctrArr.filter(c => c.status === "filed" || c.status === "submitted").length,
      draft:    ctrArr.filter(c => c.status === "draft").length,
      recentCount: ctrArr.filter(c => { try { return new Date(c.created_at) > new Date(Date.now() - 30*86400000); } catch { return false; } }).length,
      totalAmount: ctrArr.reduce((a, c) => a + (parseFloat(c.transaction_amount) || 0), 0),
    },
    watchlist: {
      total:          watchArr.length,
      byListType:     count(watchArr, "list_type"),
      byEntityType:   count(watchArr, "entity_type"),
      recentCount:    watchArr.filter(w => { try { return new Date(w.created_at) > new Date(Date.now() - 30*86400000); } catch { return false; } }).length,
    },
    customers: {
      total:      kycArr.filter(k => k.status === "approved").length,
      individual: kycArr.filter(k => k.status === "approved" && (k.type === "individual" || k.entity_type === "individual")).length,
      business:   kycArr.filter(k => k.status === "approved" && (k.type === "business"    || k.entity_type === "business")).length,
      highRisk:   kycArr.filter(k => k.status === "approved" && (k.risk_rating === "high" || k.risk_rating === "High")).length,
    },
    travelRule: {
      transactions:  trTransactions.length,
      caseFiles:     trCaseFiles.length,
      unhostedWallets: trUnhosted.length,
      byStatus:      count(trTransactions, "status"),
    },
    transactionMonitoring: {
      totalAlerts:      tmAlerts.length,
      byStatus:         count(tmAlerts, "status"),
      bySeverity:       count(tmAlerts, "severity"),
      byRule:           count(tmAlerts, "ruleName"),
      open:             tmAlerts.filter(a => a.status === "open" || a.status === "new").length,
      underReview:      tmAlerts.filter(a => a.status === "under_review").length,
      escalated:        tmAlerts.filter(a => a.status === "escalated").length,
      falsePositives:   tmAlerts.filter(a => a.status === "false_positive").length,
      truePositives:    tmAlerts.filter(a => a.status === "escalated" || a.status === "confirmed").length,
      // Alert-to-SAR conversion: escalated alerts / total alerts
      alertToSARRate:   pct(tmAlerts.filter(a => a.status === "escalated").length, tmAlerts.length),
    },
  };
}

const CHART_COLORS = [
  "#00D4FF", "#A78BFA", "#00E676", "#FF8C00", "#F0B429",
  "#FF6B9D", "#4FC3F7", "#81C784", "#FFB74D", "#CE93D8",
];

// ─── Parse ALL JSON code blocks from an AI response string ───────────────────
function extractChartConfigs(text) {
  if (!text) return [];
  const configs = [];
  // Match ```json ... ``` blocks
  const regex = /```json\s*([\s\S]*?)```/g;
  let match;
  while ((match = regex.exec(text)) !== null) {
    try {
      const parsed = JSON.parse(match[1].trim());
      if (parsed.chartType && parsed.data && Array.isArray(parsed.data)) {
        configs.push(parsed);
      }
    } catch { /* skip malformed blocks */ }
  }
  return configs;
}

// ─── Strip markdown / code fences from display text ─────────────────────────
function stripCodeBlocks(text) {
  return text
    .replace(/```json[\s\S]*?```/g, "")
    .replace(/```[\s\S]*?```/g, "")
    .replace(/\*\*(.*?)\*\*/g, "$1")
    .replace(/^#{1,3} .+/gm, "")
    .trim();
}

// ─── Recharts wrapper ────────────────────────────────────────────────────────
function ChartRenderer({ config }) {
  const C = useTheme();
  const { chartType, data, title, description, xAxisLabel, yAxisLabel, dataKey = "value", nameKey = "name" } = config;

  const commonAxis = {
    tick: { fill: C.textDim, fontSize: 10, fontFamily: "'DM Mono', monospace" },
    axisLine: { stroke: C.border },
    tickLine: false,
  };
  const tooltipStyle = {
    contentStyle: { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, fontFamily: "'DM Mono', monospace", fontSize: 12 },
    labelStyle: { color: C.text },
    itemStyle: { color: C.accent },
  };

  const renderChart = () => {
    switch (chartType) {
      case "bar":
        return (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data} margin={{ top: 4, right: 10, left: -10, bottom: 40 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
              <XAxis dataKey={nameKey} {...commonAxis} angle={-35} textAnchor="end" interval={0} label={xAxisLabel ? { value: xAxisLabel, position: "insideBottom", offset: -30, fill: C.textDim, fontSize: 10 } : undefined} />
              <YAxis {...commonAxis} label={yAxisLabel ? { value: yAxisLabel, angle: -90, position: "insideLeft", fill: C.textDim, fontSize: 10 } : undefined} />
              <Tooltip {...tooltipStyle} />
              <Bar dataKey={dataKey} radius={[4, 4, 0, 0]}>
                {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        );

      case "pie":
      case "donut":
        return (
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={data} dataKey={dataKey} nameKey={nameKey} cx="50%" cy="50%"
                innerRadius={chartType === "donut" ? 55 : 0} outerRadius={85}
                paddingAngle={2}
              >
                {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Pie>
              <Tooltip {...tooltipStyle} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 10, fontFamily: "'DM Mono', monospace", color: C.textMid }} />
            </PieChart>
          </ResponsiveContainer>
        );

      case "line":
        return (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={data} margin={{ top: 4, right: 10, left: -10, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis dataKey={nameKey} {...commonAxis} />
              <YAxis {...commonAxis} />
              <Tooltip {...tooltipStyle} />
              <Line type="monotone" dataKey={dataKey} stroke={C.accent} strokeWidth={2} dot={{ fill: C.accent, r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        );

      case "area":
        return (
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={data} margin={{ top: 4, right: 10, left: -10, bottom: 10 }}>
              <defs>
                <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={C.accent} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={C.accent} stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis dataKey={nameKey} {...commonAxis} />
              <YAxis {...commonAxis} />
              <Tooltip {...tooltipStyle} />
              <Area type="monotone" dataKey={dataKey} stroke={C.accent} fill="url(#areaGrad)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        );

      default:
        return (
          <div style={{ padding: "30px", textAlign: "center", color: C.textDim, fontSize: 12 }}>
            Chart type "{chartType}" not supported
          </div>
        );
    }
  };

  return (
    <div style={{ width: "100%" }}>
      {title && <div style={{ color: C.text, fontWeight: 700, fontSize: 13, marginBottom: 4, fontFamily: "'Syne', sans-serif" }}>{title}</div>}
      {description && <div style={{ color: C.textDim, fontSize: 11, marginBottom: 12, lineHeight: 1.5 }}>{description}</div>}
      {renderChart()}
    </div>
  );
}

// ─── Saved graph card ────────────────────────────────────────────────────────
function GraphCard({ graph, onDelete }) {
  const C = useTheme();
  function printGraph() {
    const cardEl = document.getElementById(`graph-card-${graph.id}`);
    const svgs = cardEl ? cardEl.querySelectorAll("svg") : [];
    const svgHtml = Array.from(svgs).map(svg => svg.outerHTML).join("<br>");
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${graph.title}</title><style>
    @import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=Syne:wght@700;800&display=swap');
    *{box-sizing:border-box;margin:0;padding:0}body{font-family:'DM Mono',monospace;background:#fff;color:#0A2342;padding:12mm}
    .header{display:flex;align-items:center;gap:10px;margin-bottom:6mm;padding-bottom:4mm;border-bottom:3px solid #00D4FF}
    .logo-hex{width:26px;height:26px;background:#00D4FF;clip-path:polygon(50% 0%,100% 25%,100% 75%,50% 100%,0% 75%,0% 25%)}
    .logo-text{font-family:'Syne',sans-serif;font-weight:800;font-size:16px;color:#0A2342}
    .title{font-family:'Syne',sans-serif;font-size:18px;font-weight:700;margin-bottom:1mm}
    .meta{font-size:9px;color:#4A5568;margin-bottom:6mm}
    .insight{margin-top:6mm;padding:8px 12px;background:#EBF8FF;border-left:3px solid #00D4FF;font-size:11px;line-height:1.6;color:#2D3748}
    .footer{margin-top:8mm;padding-top:2mm;border-top:1px solid #E2E8F0;font-size:8px;color:#8A9BB5;display:flex;justify-content:space-between}
    svg{max-width:100%;height:auto}
    @media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
    </style></head><body>
    <div class="header"><div class="logo-hex"></div><span class="logo-text">CrossRegGuard</span></div>
    <div class="title">${graph.title}</div>
    <div class="meta">Generated: ${graph.createdAt} · CrossReg Nexus Compliance OS</div>
    ${svgHtml}
    ${graph.insight ? `<div class="insight">${graph.insight}</div>` : ""}
    <div class="footer"><span>CrossRegGuard — Confidential</span><span>${graph.title} · ${new Date().toLocaleDateString()}</span></div>
    </body></html>`);
    w.document.close(); setTimeout(() => w.print(), 600);
  }
  return (
    <div id={`graph-card-${graph.id}`} style={{
      background: C.surface, border: `1px solid ${C.border}`,
      borderRadius: 12, padding: "18px 20px", marginBottom: 14,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
        <div>
          <div style={{ color: C.text, fontWeight: 700, fontSize: 14, fontFamily: "'Syne', sans-serif" }}>{graph.title}</div>
          <div style={{ color: C.textDim, fontSize: 11, marginTop: 2 }}>{graph.createdAt}</div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button onClick={printGraph} style={{ background: "transparent", border: `1px solid #00D4FF`, borderRadius: 6, color: "#00D4FF", padding: "3px 10px", cursor: "pointer", fontSize: 11, fontFamily: "'DM Mono', monospace" }}>⬇ PDF</button>
          <button onClick={() => onDelete(graph.id)} style={{ background: "transparent", border: "none", color: "#4A5568", cursor: "pointer", fontSize: 16 }}>✕</button>
        </div>
      </div>
      {graph.charts.map((cfg, i) => (
        <div key={i} style={{ marginBottom: i < graph.charts.length - 1 ? 20 : 0 }}>
          <ChartRenderer config={cfg} />
        </div>
      ))}
      {graph.insight && (
        <div style={{ marginTop: 12, padding: "10px 14px", background: C.accentGlow, border: `1px solid ${C.borderLight}`, borderRadius: 8, color: C.textMid, fontSize: 12, lineHeight: 1.6 }}>
          {graph.insight}
        </div>
      )}
    </div>
  );
}

// ─── Main export ─────────────────────────────────────────────────────────────
export default function KPIGraphGenerator({ alerts = [], team = [], company = {} }) {
  const C = useTheme();
  const { t } = useTranslation();

  const [moduleData,   setModuleData]   = useState(null);
  const [dataLoading,  setDataLoading]  = useState(true);

  useEffect(() => {
    loadAllModuleData(company?.id).then(data => {
      setModuleData(data);
      setDataLoading(false);
    }).catch(() => setDataLoading(false));
  }, [company?.id]);

  // ── Merge Case Management alerts with TM alerts ────────────────────────────
  const tmAlerts = (() => { try { const s = localStorage.getItem("crossregguard_tm_alerts"); return s ? JSON.parse(s) : []; } catch { return []; } })();
  const normalizedTM = tmAlerts.map(a => ({ ...a, type: a.type || a.ruleName || "TM Alert", risk: a.risk || (a.severity === "CRITICAL" ? "HIGH" : a.severity) || "MEDIUM", status: a.status || "open", source: "Transaction Monitoring" }));
  const cmAlertIds = new Set(alerts.map(a => a.id));
  const mergedAlerts = [...alerts.map(a => ({ ...a, source: a.source || "Case Management" })), ...normalizedTM.filter(a => !cmAlertIds.has(a.id))];

  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "I can create graphs from any module in the platform. Try asking me to:\n• Sanctions screening results & hit rates\n• Customer onboarding approvals vs rejections\n• SAR filings by activity type or jurisdiction\n• EDD reviews by risk level\n• Training completion rates & scores\n• Travel Rule transactions & VASP data\n• Case status & priority breakdown\n• Transaction Monitoring alerts by rule\n\nJust describe what you want to see and I'll build it.",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [savedGraphs, setSavedGraphs] = useState([]);

  useEffect(() => {
    if (!company?.id) return;
    db.from("kpi_graphs").select("*").eq("company_id", company.id)
      .order("created_at", false)
      .then(({ data, error }) => {
        if (error) {
          console.error('[KPI] Load error:', error.message);
          setSavedGraphs([]);
        } else if (data?.length) {
          setSavedGraphs(data.map(r => ({
            id: r.id, title: r.title, createdAt: r.created_at_label || r.created_at,
            charts: r.charts, insight: r.insight,
          })));
        }
      })
      .catch(e => {
        console.error('[KPI] Load failed:', e.message);
        setSavedGraphs([]);
      });
  }, [company?.id]);

  const chatRef = useRef(null);

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [messages]);

  // Build a comprehensive data summary across all modules
  const alertsByType    = mergedAlerts.reduce((a, x) => { a[x.type] = (a[x.type] || 0) + 1; return a; }, {});
  const alertsByCountry = mergedAlerts.reduce((a, x) => { if (x.country) a[x.country] = (a[x.country] || 0) + 1; return a; }, {});
  const alertsByStatus  = mergedAlerts.reduce((a, x) => { a[x.status] = (a[x.status] || 0) + 1; return a; }, {});

  const teamMembers = (team || []).map(m => ({
    id:         m.id || m.user_id || "",
    name:       `${m.firstName || m.first_name || ""} ${m.lastName || m.last_name || ""}`.trim() || m.email || "Unknown",
    email:      m.email || "",
    role:       m.role || m.permission || "Analyst",
    permission: m.permission || "",
  }));

  // Build per-user training breakdown (join user IDs from training_progress with team names)
  const trainingByUser = teamMembers.length > 0 && moduleData?.training?.completedByUserId
    ? teamMembers.map(m => ({
        name: m.name,
        modulesCompleted: moduleData.training.completedByUserId[m.id] || 0,
        modulesEnrolled:  moduleData.training.enrolledByUserId?.[m.id]  || 0,
        totalModules:     moduleData.training.totalModules || 0,
        completionRate:   moduleData.training.totalModules
          ? `${Math.round(((moduleData.training.completedByUserId[m.id] || 0) / moduleData.training.totalModules) * 100)}%`
          : "0%",
        quizPassed: (moduleData.training.completedByUserId[m.id] || 0) > 0,
      }))
    : [];

  const dataSummary = {
    companyName: company?.name || "Your Organization",
    teamSize: teamMembers.length,
    teamMembers,
    alerts: {
      total: mergedAlerts.length,
      open:  mergedAlerts.filter(a => a.status === "open" || a.status === "new").length,
      highRisk: mergedAlerts.filter(a => a.risk === "HIGH" || a.risk === "Critical").length,
      byType: alertsByType,
      byCountry: alertsByCountry,
      byStatus: alertsByStatus,
      fromCaseManagement: alerts.length,
      fromTransactionMonitoring: normalizedTM.filter(a => !cmAlertIds.has(a.id)).length,
    },
    ...(moduleData || {}),
    // Augment training with named per-user breakdown
    training: moduleData?.training ? {
      ...moduleData.training,
      byUser: trainingByUser,
    } : undefined,
  };

  async function sendMessage() {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput("");

    const newMessages = [...messages, { role: "user", content: userMsg }];
    setMessages(newMessages);
    setLoading(true);

    try {
      const systemPrompt = `You are a KPI graph generator for an AML/compliance platform called CrossRegGuard. You have access to live data from every module in the platform.

When the user asks for a visualization, respond with:
1. A brief description of what you're creating
2. One or more JSON code blocks (wrapped in \`\`\`json ... \`\`\`) with chart configurations
3. Key compliance insights about the data

Each JSON block must follow this exact structure:
{
  "chartType": "bar" | "pie" | "donut" | "line" | "area",
  "title": "Chart Title",
  "description": "Brief description",
  "xAxisLabel": "X Axis Label",
  "yAxisLabel": "Y Axis Label",
  "data": [{ "name": "Label", "value": 42 }, ...],
  "dataKey": "value",
  "nameKey": "name"
}

LIVE DATA FROM ALL MODULES:
${JSON.stringify(dataSummary, null, 2)}

AVAILABLE DATA MODULES:
- Cases: status (open/closed/escalated), priority, type, country, risk rating breakdowns
- SAR Filings: status, activity type, jurisdiction, filed vs pending, alert-to-SAR conversion rate
- EDD Reviews: status, risk level, jurisdiction
- KYC/Onboarding: approval rate (${dataSummary.kyc?.approvalRate || "N/A"}), rejection rate, risk ratings, entity types, screening status
- Sanctions Screening: hit rate (${dataSummary.sanctions?.hitRate || "N/A"}), false positive rate (${dataSummary.sanctions?.falsePositiveRate || "N/A"}) — confirmed hits: ${dataSummary.sanctions?.confirmedHits || 0}, false positives: ${dataSummary.sanctions?.falsePositives || 0}
- Training: completion rate (${dataSummary.training?.completionRate || "N/A"}), by user, by category
- Travel Rule: transactions, VASP case files, unhosted wallet assessments
- Transaction Monitoring: alerts by rule/severity/status, false positives: ${dataSummary.transactionMonitoring?.falsePositives || 0}, escalated: ${dataSummary.transactionMonitoring?.escalated || 0}, alert-to-SAR rate: ${dataSummary.transactionMonitoring?.alertToSARRate || "N/A"}
- CTR Filings: ${dataSummary.ctr?.total || 0} total, ${dataSummary.ctr?.filed || 0} filed, status breakdown, total transaction amounts
- Internal Watchlist: ${dataSummary.watchlist?.total || 0} entries, by list type and entity type
- Approved Customers: ${dataSummary.customers?.total || 0} total (${dataSummary.customers?.individual || 0} individual, ${dataSummary.customers?.business || 0} business), ${dataSummary.customers?.highRisk || 0} high-risk

For false positive rate graphs: use sanctions.falsePositiveRate and transactionMonitoring.falsePositives/totalAlerts. For alert-to-SAR conversion: use transactionMonitoring.alertToSARRate. For case closure rates: use cases.byStatus.
- Alerts (combined): ${mergedAlerts.length} total across Case Management + Transaction Monitoring
- Team Members (${teamMembers.length}): ${teamMembers.map(m => `${m.name} (${m.role})`).join(", ") || "None loaded"}

IMPORTANT: Use ONLY the real names and data from teamMembers above. Never invent names or fabricate statistics. If data for a specific person is not in the dataset, say so rather than making it up.

IMPORTANT: Always include at least one JSON block with real data from the context above. Use actual numbers from the data. If asked for a module with no data yet, create a realistic illustrative example and note it is sample data.`;


      // Build message history for the API (last 10 messages)
      const apiMessages = newMessages.slice(-10).map(m => ({
        role: m.role === "assistant" ? "assistant" : "user",
        content: m.content,
      }));

      const rawText = await callAI({
        system: systemPrompt,
        messages: apiMessages,
        maxTokens: 2000,
      });

      if (!rawText) throw new Error("Empty response from API");

      // Add assistant message to chat
      setMessages(prev => [...prev, { role: "assistant", content: rawText }]);

      // Extract and save any chart configs found in the response
      const charts = extractChartConfigs(rawText);
      if (charts.length > 0) {
        const graphTitle = charts[0].title || userMsg.slice(0, 50);
        const insight = stripCodeBlocks(rawText).slice(0, 400) || null;

        const newGraph = {
          id: Date.now(),
          title: graphTitle,
          createdAt: new Date().toLocaleString(),
          charts,
          insight,
        };
        setSavedGraphs(prev => [newGraph, ...prev]);
        if (company?.id) {
          db.from("kpi_graphs").insert({
            id: String(newGraph.id),
            company_id: company.id,
            title: graphTitle,
            charts: JSON.stringify(charts),
            insight: insight || null,
            created_at_label: newGraph.createdAt,
          }).then(({ error }) => {
            if (error) console.warn("[KPI] Save graph error:", error.message);
          }).catch(e => console.warn("[KPI] Save graph failed:", e.message));
        }
      }

    } catch (err) {
      setMessages(prev => [...prev, {
        role: "assistant",
        content: `Error: ${err.message}`,
      }]);
    } finally {
      setLoading(false);
    }
  }

  function deleteGraph(id) {
    setSavedGraphs(prev => prev.filter(g => g.id !== id));
    if (company?.id) {
      db.from("kpi_graphs").delete().eq("id", String(id)).eq("company_id", company.id)
        .then(({ error }) => {
          if (error) console.warn("[KPI] Delete graph error:", error.message);
        })
        .catch(e => console.warn("[KPI] Delete graph failed:", e.message));
    }
  }

  function clearAll() {
    if (window.confirm("Clear all saved graphs?")) {
      setSavedGraphs([]);
      if (company?.id) {
        db.from("kpi_graphs").delete().eq("company_id", company.id)
          .then(({ error }) => {
            if (error) console.warn("[KPI] Clear all graphs error:", error.message);
          })
          .catch(e => console.warn("[KPI] Clear all graphs failed:", e.message));
      }
    }
  }

  // Render message content — show clean text, suppress raw JSON blocks
  function renderMessage(msg) {
    if (msg.role === "user") return msg.content;
    return stripCodeBlocks(msg.content) || "(Chart generated — see left panel)";
  }

  return (
    <div style={{ fontFamily: "'DM Mono', monospace", display: "flex", gap: 20, height: "calc(100vh - 120px)", minHeight: 500 }}>

      {/* ── LEFT: Saved graphs ── */}
      <div style={{
        width: 400, flexShrink: 0,
        display: "flex", flexDirection: "column",
        background: C.surface, border: `1px solid ${C.border}`,
        borderRadius: 14, overflow: "hidden",
      }}>
        <div style={{ padding: "16px 20px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
          <div>
            <div style={{ color: C.text, fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: 16 }}>Saved Graphs</div>
            <div style={{ color: C.textDim, fontSize: 11, marginTop: 2 }}>{savedGraphs.length} visualizations</div>
          </div>
          {savedGraphs.length > 0 && (
            <button onClick={clearAll} style={{ background: "transparent", border: `1px solid ${C.border}`, borderRadius: 6, color: C.textDim, cursor: "pointer", fontSize: 11, padding: "4px 10px", fontFamily: "'DM Mono', monospace" }}>
              Clear All
            </button>
          )}
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "16px" }}>
          {savedGraphs.length === 0 ? (
            <div style={{ textAlign: "center", padding: "60px 20px", color: C.textDim }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>📊</div>
              <div style={{ fontSize: 13, marginBottom: 6, color: C.textMid }}>No graphs yet</div>
              <div style={{ fontSize: 12 }}>Ask the AI to create a visualization and it will appear here</div>
            </div>
          ) : (
            savedGraphs.map(g => <GraphCard key={g.id} graph={g} onDelete={deleteGraph} />)
          )}
        </div>
      </div>

      {/* ── RIGHT: Chat ── */}
      <div style={{
        flex: 1,
        display: "flex", flexDirection: "column",
        background: C.surface, border: `1px solid ${C.border}`,
        borderRadius: 14, overflow: "hidden",
      }}>
        {/* Header */}
        <div style={{ padding: "16px 20px", borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
          <div style={{ color: C.text, fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: 16 }}>{t('kpiGraphs.title')}</div>
          <div style={{ color: C.textDim, fontSize: 11, marginTop: 2 }}>
            {dataLoading ? "Loading module data…" : `Cases · KYC · SAR · EDD · Sanctions · Training · Travel Rule · TM · ${dataSummary.teamSize} team members`}
          </div>
        </div>

        {/* Messages */}
        <div ref={chatRef} style={{ flex: 1, overflowY: "auto", padding: "20px", display: "flex", flexDirection: "column", gap: 14 }}>
          {messages.map((msg, i) => (
            <div key={i} style={{ display: "flex", justifyContent: msg.role === "user" ? "flex-end" : "flex-start" }}>
              <div style={{
                maxWidth: "85%",
                background: msg.role === "user" ? C.accent : C.surfaceAlt,
                color: msg.role === "user" ? C.bg : C.text,
                borderRadius: msg.role === "user" ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
                padding: "12px 16px",
                fontSize: 13,
                lineHeight: 1.6,
                whiteSpace: "pre-wrap",
                border: msg.role === "assistant" ? `1px solid ${C.border}` : "none",
              }}>
                {renderMessage(msg)}
              </div>
            </div>
          ))}

          {loading && (
            <div style={{ display: "flex", justifyContent: "flex-start" }}>
              <div style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: "14px 14px 14px 4px", padding: "12px 16px", display: "flex", gap: 6, alignItems: "center" }}>
                {[0, 1, 2].map(i => (
                  <div key={i} style={{ width: 6, height: 6, borderRadius: "50%", background: C.accent, opacity: 0.6, animation: `kpiBounce 1s ${i * 0.2}s infinite` }} />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Input */}
        <div style={{ padding: "16px 20px", borderTop: `1px solid ${C.border}`, flexShrink: 0, display: "flex", gap: 10 }}>
          <input
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && !e.shiftKey && sendMessage()}
            placeholder="Ask for a chart, e.g. 'show alert types as a bar chart'..."
            disabled={loading}
            style={{
              flex: 1, padding: "10px 16px",
              background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8,
              color: C.text, fontFamily: "'DM Mono', monospace", fontSize: 13,
              outline: "none",
            }}
          />
          <button
            onClick={sendMessage}
            disabled={loading || !input.trim()}
            style={{
              background: loading || !input.trim() ? C.border : C.accent,
              color: C.bg, border: "none", borderRadius: 8,
              padding: "10px 20px", cursor: loading || !input.trim() ? "not-allowed" : "pointer",
              fontFamily: "'DM Mono', monospace", fontWeight: 700, fontSize: 13,
              transition: "background 0.15s",
            }}
          >
            {loading ? "..." : "Send"}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes kpiBounce {
          0%, 100% { transform: translateY(0); opacity: 0.4; }
          50% { transform: translateY(-5px); opacity: 1; }
        }
      `}</style>
    </div>
  );
}